import React, { useState } from "react";
import AuthenticityTokenField from "../../../components/forms/AuthenticityTokenField";
import { DashboardPageLayout } from "../../../components/layout/DashboardPageLayout";
import { Button, ButtonLink } from "../../../components/ui/button";
import { Card, CardContent } from "../../../components/ui/card";
import {
  FieldLabel,
  TextAreaInput,
  TextInput,
} from "../../../components/ui/FormControls";
import type { MainAppPageProps } from "../types";
import { CredentialDateFields } from "./CredentialDateFields";
import { CredentialRemindersFields } from "./CredentialRemindersFields";
import { CredentialParsePendingState } from "./CredentialParsePendingState";
import { CredentialPhotoDropzone } from "./CredentialPhotoDropzone";
import { CREDENTIAL_CATEGORY_LABELS } from "@toolkit/mobile-shared";

type CredentialNewPageProps = Pick<
  MainAppPageProps,
  "csrfToken" | "routes" | "credentialForm" | "credentialHolders"
>;

export const CredentialNewPage: React.FC<CredentialNewPageProps> = ({
  csrfToken,
  routes,
  credentialForm,
  credentialHolders = [],
}) => {
  const photoAction =
    routes.dashboardCredentialFromPhoto ?? "/dashboard/credentials/from-photo";
  const manualAction = routes.dashboardCredentials ?? "/dashboard/credentials";
  const listPath = routes.dashboardCredentials ?? "/dashboard/credentials";
  const [busy, setBusy] = useState(false);

  return (
    <DashboardPageLayout
      trail={[
        { label: "Credentials", href: listPath },
        { label: "Add a credential" },
      ]}
      title="Add a credential"
    >
      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="bg-card">
          <CardContent className="relative pt-6">
            {busy ? <CredentialParsePendingState overlay /> : null}
            <form
              action={photoAction}
              method="post"
              encType="multipart/form-data"
              className="flex flex-col gap-5"
              onSubmit={() => {
                setBusy(true);
              }}
            >
              <fieldset
                aria-busy={busy}
                className="flex flex-col gap-5 border-0 p-0"
              >
                <AuthenticityTokenField csrfToken={csrfToken} />
                {credentialHolders.length > 0 ? (
                  <div>
                    <FieldLabel htmlFor="photo_user_id">
                      Whose is this?
                    </FieldLabel>
                    <select
                      id="photo_user_id"
                      name="user_id"
                      defaultValue={credentialForm?.userId ?? ""}
                      className="ds-input"
                    >
                      {credentialHolders.map((holder) => (
                        <option key={holder.id} value={holder.id}>
                          {holder.name}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}
                <CredentialPhotoDropzone />
                <Button type="submit" variant="premium" disabled={busy}>
                  {busy ? "Reading this photo…" : "Scan Credential"}
                </Button>
                <ButtonLink
                  href="#credential-manual"
                  variant="outline"
                  className="w-full justify-center"
                >
                  Enter manually
                </ButtonLink>
              </fieldset>
            </form>
          </CardContent>
        </Card>

        <Card id="credential-manual" className="bg-card scroll-mt-6">
          <CardContent className="pt-6">
            <form
              action={photoAction}
              method="post"
              encType="multipart/form-data"
              className="mb-6 flex flex-col gap-2"
            >
              <AuthenticityTokenField csrfToken={csrfToken} />
              <input type="hidden" name="user_id" value="" />
              <FieldLabel htmlFor="credential_photo">Add photo</FieldLabel>
              <p className="text-sm text-muted-foreground">
                Optional. We read it and fill in the details.
              </p>
              <input
                id="credential_photo"
                name="photo"
                type="file"
                accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                className="text-sm text-foreground"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  const form = event.currentTarget.form;
                  if (!file || !form) return;
                  const holder = document.getElementById(
                    "credential_user_id",
                  ) as HTMLSelectElement | null;
                  const hidden = form.querySelector(
                    'input[name="user_id"]',
                  ) as HTMLInputElement | null;
                  if (hidden && holder?.value) hidden.value = holder.value;
                  setBusy(true);
                  form.submit();
                }}
              />
            </form>
            <form action={manualAction} method="post">
              <fieldset
                disabled={busy}
                className="flex flex-col gap-4 disabled:opacity-60"
              >
                <AuthenticityTokenField csrfToken={csrfToken} />
                <p className="text-sm font-medium text-foreground">
                  Or type it in
                </p>
                {credentialHolders.length > 0 ? (
                  <div>
                    <FieldLabel htmlFor="credential_user_id">Holder</FieldLabel>
                    <select
                      id="credential_user_id"
                      name="credential[user_id]"
                      defaultValue={credentialForm?.userId ?? ""}
                      className="ds-input"
                    >
                      {credentialHolders.map((holder) => (
                        <option key={holder.id} value={holder.id}>
                          {holder.name}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}
                <div>
                  <FieldLabel htmlFor="credential_name">Name</FieldLabel>
                  <TextInput
                    id="credential_name"
                    name="credential[name]"
                    defaultValue={credentialForm?.name ?? ""}
                    placeholder="OSHA 30"
                    required
                  />
                </div>
                <div>
                  <FieldLabel htmlFor="credential_issuer">Issuer</FieldLabel>
                  <TextInput
                    id="credential_issuer"
                    name="credential[issuer]"
                    defaultValue={credentialForm?.issuer ?? ""}
                    placeholder="OSHA"
                  />
                </div>
                <div>
                  <FieldLabel htmlFor="credential_number">Number</FieldLabel>
                  <TextInput
                    id="credential_number"
                    name="credential[credential_number]"
                    defaultValue={credentialForm?.credentialNumber ?? ""}
                  />
                </div>
                <div>
                  <FieldLabel htmlFor="credential_category">Type</FieldLabel>
                  <select
                    id="credential_category"
                    name="credential[category]"
                    defaultValue={credentialForm?.category ?? "other"}
                    className="ds-input"
                  >
                    {Object.entries(CREDENTIAL_CATEGORY_LABELS).map(
                      ([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ),
                    )}
                  </select>
                </div>
                <CredentialDateFields
                  defaultIssuedOn={credentialForm?.issuedOn ?? ""}
                  defaultExpiresOn={credentialForm?.expiresOn ?? ""}
                />
                <div>
                  <FieldLabel htmlFor="credential_notes">Notes</FieldLabel>
                  <TextAreaInput
                    id="credential_notes"
                    name="credential[notes]"
                    rows={4}
                    defaultValue={credentialForm?.notes ?? ""}
                    placeholder="Anything else on the card — restrictions, endorsements, or context"
                  />
                </div>
                <CredentialRemindersFields confirmed={false} />
                <Button type="submit" variant="outline">
                  Save
                </Button>
              </fieldset>
            </form>
          </CardContent>
        </Card>
      </div>
    </DashboardPageLayout>
  );
};
